import type React from "react";
import {
  Briefcase,
  Building,
  Building2,
  FileText,
  Handshake,
  LayoutDashboard,
  MapPin,
  MessageSquare,
  Monitor,
  Send,
  Settings,
  TrendingUp,
  UserCog,
  Users,
} from "lucide-react";

export interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  /** Hide from non-admin nav (matches canAccess for sales / configuration). */
  adminOnly?: boolean;
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

/** Shared nav sections for desktop Sidebar and mobile More sheet. */
export const navSections: NavSection[] = [
  {
    title: "Sales & Pipeline",
    items: [
      { label: "Dashboard", path: "/", icon: LayoutDashboard },
      { label: "Leads & Contacts", path: "/contacts", icon: Users },
      { label: "Inbox", path: "/inbox", icon: MessageSquare },
      { label: "Deals & Sales", path: "/deals", icon: Briefcase },
    ],
  },
  {
    title: "Property Operations",
    items: [
      { label: "Projects Master", path: "/projects", icon: Building2 },
      { label: "Properties & Inventory", path: "/properties", icon: Building },
      { label: "Site Visits", path: "/site_visits", icon: MapPin },
      { label: "Walk-In Kiosk", path: "/kiosk", icon: Monitor },
    ],
  },
  {
    title: "Channels & Partners",
    items: [
      { label: "Channel Partners", path: "/channel_partners", icon: Handshake },
    ],
  },
  {
    title: "Insights & Campaigns",
    items: [
      {
        label: "Analytics Suite",
        path: "/analytics",
        icon: TrendingUp,
        adminOnly: true,
      },
      {
        label: "WhatsApp Campaigns",
        path: "/campaigns",
        icon: Send,
        adminOnly: true,
      },
      { label: "Payment Demands", path: "/bookings", icon: FileText },
    ],
  },
  {
    title: "System",
    items: [
      {
        label: "Team Management",
        path: "/sales",
        icon: UserCog,
        adminOnly: true,
      },
      {
        label: "Settings",
        path: "/settings",
        icon: Settings,
        adminOnly: true,
      },
    ],
  },
];

/** Drop admin-only items (and empty sections) for non-admins. */
export function filterNavSections(
  sections: NavSection[],
  isAdmin: boolean,
): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => isAdmin || !item.adminOnly),
    }))
    .filter((section) => section.items.length > 0);
}

/** Paths that map to a primary bottom-bar tab (not "More"). */
const PRIMARY_TAB_MATCHERS: Array<(pathname: string) => boolean> = [
  (p) => p === "/",
  (p) => p === "/contacts" || p.startsWith("/contacts/"),
  (p) =>
    p === "/site_visits" ||
    p.startsWith("/site_visits/") ||
    p === "/site-visits" ||
    p.startsWith("/site-visits/"),
];

export function isRouteActive(pathname: string, path: string): boolean {
  if (path === "/" && pathname === "/") return true;
  if (path !== "/" && pathname.startsWith(path)) return true;
  if (path === "/site_visits" && pathname.startsWith("/site-visits"))
    return true;
  if (path === "/channel_partners" && pathname.startsWith("/channel-partners"))
    return true;
  return false;
}

/** True when the current path should highlight the More tab. */
export function isMoreRouteActive(pathname: string): boolean {
  if (PRIMARY_TAB_MATCHERS.some((match) => match(pathname))) return false;
  return (
    pathname.startsWith("/deals") ||
    pathname.startsWith("/projects") ||
    pathname.startsWith("/properties") ||
    pathname.startsWith("/channel_partners") ||
    pathname.startsWith("/channel-partners") ||
    pathname.startsWith("/analytics") ||
    pathname.startsWith("/bookings") ||
    pathname.startsWith("/campaigns") ||
    pathname.startsWith("/inbox") ||
    pathname.startsWith("/kiosk") ||
    pathname.startsWith("/settings") ||
    pathname.startsWith("/tasks") ||
    pathname.startsWith("/companies") ||
    pathname.startsWith("/sales") ||
    pathname.startsWith("/tags")
  );
}
